/**
 * Collections Service
 *
 * Fetch API implementation of the CollectionsFactory.
 * Migrated from: assets/js/services.js - CollectionsFactory
 */
import { fetchSessionJson } from '../../services/SessionRequest';
import { stringifyJson, toRequiredPositiveInteger } from '../../services/Utilities';
import type { MasterTemplateCardProps } from '../../components/Experiences/Cards/MasterTemplateCard/MasterTemplateCard';

const API_SERVER = process.env.API_SERVER || '';

interface LibraryTemplateFields {
  template_id?: number | string;
  image_filename?: string;
  is_guided?: boolean | number;
  has_groups?: boolean | number;
  reading_group_id?: number | string;
  standard_sets?: string | null;
  gradeId?: number | string;
  grade_id?: number | string;
  standard_set_id?: number | string;
  standard_set_label?: string;
  standards?: MasterTemplateCardProps['standards'];
  labels?: MasterTemplateCardProps['labels'];
  parents?: string[];
  permissions?: string[];
  source?: string;
  is_public?: boolean;
  ai_preview_state?: string | null;
}

export interface CollectionMember extends LibraryTemplateFields {
  id: number | string;
  name: string;
  description?: string;
  image_url?: string;
  kind: 'collection' | 'template';
  members?: CollectionMember[];
  [key: string]: unknown;
}

export interface Collection {
  id: number | string;
  name: string;
  description?: string;
  image_url?: string;
  kind?: 'collection' | 'template';
  members?: CollectionMember[];
  [key: string]: unknown;
}

export interface ClassCollectionsResponse {
  collections?: Collection[];
  [key: string]: unknown;
}

export interface Template extends LibraryTemplateFields {
  id: number | string;
  name: string;
  description?: string;
  image_url?: string;
  [key: string]: unknown;
}

export interface UserCollectionsResponse {
  collections?: Collection[];
  templates?: Template[];
  [key: string]: unknown;
}

export interface AssignableClass {
  class_id: number;
  class_name: string;
  [key: string]: unknown;
}

export interface OrgCollection {
  uuid: string;
  isSubscribed?: boolean;
  collection_uuid?: string;
  name?: string;
  description?: string;
  image_filename?: string;
  [key: string]: unknown;
}

export interface OrgCollectionWithLibraryFlag extends OrgCollection {
  inLibrary: boolean;
}

/**
 * Fetches assignable collections for a specific class.
 * @param classId - The class ID to fetch collections for
 * @returns Promise resolving to the class collections response
 */
export async function getClassCollections(
  classId: number
): Promise<ClassCollectionsResponse> {
  const validatedClassId = toRequiredPositiveInteger(classId, 'classId');

  const url = `${API_SERVER}/experiences/classes/${encodeURIComponent(validatedClassId)}/assignable`;

  return fetchSessionJson<ClassCollectionsResponse>(url, 'Failed to fetch class collections');
}

/**
 * Fetches assignable collections for the current user.
 * @returns Promise resolving to the user collections response
 */
export async function getUserCollections(): Promise<UserCollectionsResponse> {
  const url = `${API_SERVER}/experiences/users/assignable`;

  return fetchSessionJson<UserCollectionsResponse>(url, 'Failed to fetch user collections');
}

/**
 * Checks which classes are assignable for the given data.
 * @param data - The data to check assignability for
 * @returns Promise resolving to an array of assignable classes
 */
export async function checkAssignableClasses(
  data: Record<string, unknown>
): Promise<AssignableClass[]> {
  const url = `${API_SERVER}/experiences/classes/check-assignable`;

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: stringifyJson(data),
  });

  if (!response.ok) {
    throw new Error(`Failed to check assignable classes: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetches available collections for the selected class.
 * @param classId - The selected class ID
 * @returns Promise resolving to an array of available collections
 */
export async function getAvailableCollections(classId: number): Promise<OrgCollection[]> {
  const validatedClassId = toRequiredPositiveInteger(classId, 'classId');

  const url = `${API_SERVER}/users/classes/${encodeURIComponent(validatedClassId)}/available_collections`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch available collections: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

/**
 * Enables a collection for the selected class.
 * @param uuid - The collection UUID to enable
 * @param classId - The selected class ID
 * @returns Promise resolving when the collection is enabled
 */
export async function addCollection(uuid: string, classId: number): Promise<void> {
  if (uuid == null || !uuid.trim()) {
    throw new Error('uuid must be non-null and not empty');
  }

  const validatedClassId = toRequiredPositiveInteger(classId, 'classId');

  const url = `${API_SERVER}/users/classes/${encodeURIComponent(validatedClassId)}/enable-collection/${encodeURIComponent(uuid)}`;

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to enable collection: ${response.status} ${response.statusText}`);
  }
}

/**
 * Disables a collection for the selected class.
 * @param uuid - The collection UUID to disable
 * @param classId - The selected class ID
 * @returns Promise resolving when the collection is disabled
 */
export async function removeCollection(uuid: string, classId: number): Promise<void> {
  if (uuid == null || !uuid.trim()) {
    throw new Error('uuid must be a non-null, not empty string');
  }

  const validatedClassId = toRequiredPositiveInteger(classId, 'classId');

  const url = `${API_SERVER}/users/classes/${encodeURIComponent(validatedClassId)}/disable-collection/${encodeURIComponent(uuid)}`;

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to disable collection: ${response.status} ${response.statusText}`);
  }
}

const CollectionsService = {
  getClassCollections,
  getUserCollections,
  checkAssignableClasses,
  getAvailableCollections,
  addCollection,
  removeCollection,
};

export default CollectionsService;
